import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMG } from "@/lib/api";
import { Logo } from "./Logo";

export const FinalCta = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="final-cta-section">
      <motion.img style={{ y }} src={IMG.marble} alt="Gleaming marble kitchen" className="absolute inset-0 h-[124%] w-full -translate-y-[12%] object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C] via-[#090A0C]/60 to-[#090A0C]" />
      <div className="relative mx-auto max-w-[1400px] px-5 py-32 sm:px-8 sm:py-44">
        <div className="mb-10 w-36 sm:w-44 lg:absolute lg:right-8 lg:top-1/2 lg:mb-0 lg:w-[380px] lg:-translate-y-1/2">
          <motion.img src="/images/logo-lg.png" alt="Clean As Fuck Cleaning Co. emblem" data-testid="cta-logo-emblem"
            initial={{ opacity: 0, rotate: -12, scale: 0.9 }} whileInView={{ opacity: 1, rotate: 0, scale: 1 }} viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full rounded-full shadow-[0_30px_80px_rgba(0,0,0,0.7)]" />
        </div>
        <p className="eyebrow mb-8 text-zinc-300">( Ready when you are )</p>
        <h2 className="font-display text-[16vw] uppercase leading-[0.86] text-zinc-50 sm:text-[11vw] lg:text-[9rem]">
          Your mess.<br /><span className="font-serif-i normal-case text-acid">Our obsession.</span>
        </h2>
        <Link to="/book" data-testid="final-cta-book-button"
          className="group mt-12 inline-flex items-center gap-4 rounded-full bg-acid py-5 pl-8 pr-5 text-lg font-bold text-black transition-transform duration-300 hover:scale-[1.04]">
          Book your clean
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black text-[var(--acid)] transition-transform duration-500 group-hover:rotate-45">
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </Link>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer className="border-t border-white/10 bg-[#090A0C]" data-testid="site-footer">
    <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-14 sm:px-8 md:grid-cols-3">
      <div>
        <Logo />
        <p className="mt-5 max-w-xs text-sm text-zinc-400">Luxury residential and commercial cleaning. Unapologetically immaculate.</p>
      </div>
      <div className="flex flex-col gap-2 text-sm text-zinc-300">
        <span className="eyebrow mb-2">Contact</span>
        <a href="tel:+19704023530" className="transition-colors hover:text-[var(--acid)]" data-testid="footer-phone">970-402-3530</a>
        <a href="mailto:wowthatscleanasfuck@gmail.com" className="break-all transition-colors hover:text-[var(--acid)]" data-testid="footer-email">wowthatscleanasfuck@gmail.com</a>
        <span data-testid="footer-area">Serving all of Northern Colorado · Based in Fort Collins</span>
      </div>
      <div className="flex flex-col gap-2 text-sm text-zinc-300 md:items-end">
        <span className="eyebrow mb-2">Booking times</span>
        <span>Any hour, any day · Closed Sundays</span>
        <Link to="/admin/login" className="mt-4 text-zinc-400 transition-colors hover:text-[var(--acid)]" data-testid="footer-admin-link">Owner login</Link>
      </div>
    </div>
    <div className="mx-auto flex max-w-[1400px] justify-between border-t border-white/10 px-5 py-6 font-mono-x text-xs text-zinc-400 sm:px-8">
      <span>© {new Date().getFullYear()} Clean As Fuck Cleaning Co. LLC</span>
      <span className="hidden sm:inline">Stay filthy. Live clean.</span>
    </div>
  </footer>
);
